/**
 * Registry for text annotation editors.
 *
 * Maps annotation IDs to callbacks that re-enter editing mode,
 * and maps mounted Y.XmlFragment instances to annotation IDs so the
 * global undo handler can refocus the correct annotation when
 * an undo/redo affects visible text content.
 */

import * as Sentry from '@sentry/nextjs'
import * as Y from 'yjs'
import { suppressTextUndoTrackingDuringRefocus, resumeTextUndoTrackingAfterRefocus } from './undoManager'

type EditorEntry = {
  enterEditing: () => void
  exitEditing: () => void
}

const ANNOTATION_CONTENT_PREFIX = 'annotation-content-'

const editorRegistry = new Map<string, EditorEntry>()
const fragmentToAnnotationId = new Map<Y.XmlFragment, string>()

/** The annotation ID that was most recently refocused by undo/redo */
let undoRefocusedAnnotationId: string | null = null

export function registerAnnotationEditor(annotationId: string, fragment: Y.XmlFragment, entry: EditorEntry): void {
  editorRegistry.set(annotationId, entry)
  fragmentToAnnotationId.set(fragment, annotationId)
}

export function unregisterAnnotationEditor(annotationId: string, fragment: Y.XmlFragment): void {
  editorRegistry.delete(annotationId)
  fragmentToAnnotationId.delete(fragment)
  if (undoRefocusedAnnotationId === annotationId) {
    undoRefocusedAnnotationId = null
  }
}

function getAnnotationIdForType(type: unknown): string | null {
  if (!(type instanceof Y.AbstractType)) return null

  // Walk up to the root using Yjs internals (_item.parent).
  // This is necessary because Y.findRootTypeKey throws on non-root types,
  // and doc.getXmlFragment() throws when the root key is a Map, not a Fragment.
  let current: Y.AbstractType<unknown> = type
  while (current._item?.parent instanceof Y.AbstractType) {
    current = current._item.parent
  }

  if (!(current instanceof Y.XmlFragment)) return null

  // Fast path: check mounted fragment registry
  const mountedAnnotationId = fragmentToAnnotationId.get(current)
  if (mountedAnnotationId) return mountedAnnotationId

  // Slow path: derive annotation ID from root key
  try {
    const rootKey = Y.findRootTypeKey(current)
    if (typeof rootKey === 'string' && rootKey.startsWith(ANNOTATION_CONTENT_PREFIX)) {
      return rootKey.slice(ANNOTATION_CONTENT_PREFIX.length)
    }
  } catch {
    // findRootTypeKey can throw for detached or unexpected types
  }

  return null
}

function getAffectedAnnotationIds(changedParentTypes: Map<unknown, unknown>): string[] {
  const annotationIds = new Set<string>()

  for (const [type] of changedParentTypes) {
    const annotationId = getAnnotationIdForType(type)
    if (annotationId) {
      annotationIds.add(annotationId)
    }
  }

  return Array.from(annotationIds)
}

/**
 * Returns true if every changed parent type in the event belongs to a
 * text annotation fragment (i.e. the undo/redo only affected
 * text content, not canvas/timeline state).
 */
export function isTextOnlyUndoEvent(changedParentTypes: Map<unknown, unknown>): boolean {
  if (changedParentTypes.size === 0) return false

  let sawTextAnnotation = false
  for (const [type] of changedParentTypes) {
    const annotationId = getAnnotationIdForType(type)
    if (!annotationId) {
      return false
    }
    sawTextAnnotation = true
  }

  return sawTextAnnotation
}

/**
 * Given a set of changedParentTypes from a Yjs undo/redo event,
 * refocus the affected annotation when exactly one visible text
 * annotation changed. If the undo/redo did not affect a single visible
 * annotation, exit editing on any previously undo-refocused annotation.
 */
export function refocusAffectedAnnotation(changedParentTypes: Map<unknown, unknown>): void {
  const affectedAnnotationIds = getAffectedAnnotationIds(changedParentTypes)

  if (affectedAnnotationIds.length === 1) {
    const annotationId = affectedAnnotationIds[0]
    const entry = editorRegistry.get(annotationId)
    if (entry) {
      undoRefocusedAnnotationId = annotationId
      // Suppress ySyncPluginKey tracking while the editor re-syncs with
      // the restored Y.XmlFragment — prevents sync mutations from being
      // captured as new undo items (which would clear the redo stack).
      suppressTextUndoTrackingDuringRefocus()
      requestAnimationFrame(() => {
        // The editor may have unmounted before this rAF fires (e.g. rapid
        // undo removed the annotation). Re-validate the registry entry.
        const currentEntry = editorRegistry.get(annotationId)
        if (!currentEntry || currentEntry !== entry) {
          resumeTextUndoTrackingAfterRefocus()
          return
        }
        try {
          currentEntry.enterEditing()
        } catch (err) {
          Sentry.captureException(err, { tags: { context: 'text-undo-refocus' } })
          resumeTextUndoTrackingAfterRefocus()
          return
        }
        // Two rAFs for React to commit the state update and the editor
        // sync plugin to finish writing, then resume normal tracking.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            resumeTextUndoTrackingAfterRefocus()
          })
        })
      })
      return
    }
  }

  if (undoRefocusedAnnotationId) {
    const entry = editorRegistry.get(undoRefocusedAnnotationId)
    undoRefocusedAnnotationId = null
    if (entry) {
      requestAnimationFrame(() => {
        entry.exitEditing()
      })
    }
  }
}
